import { NewspaperIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useEffect, useMemo } from 'react'
import {
    AccountRecord,
    useGetSocialMediaServicesQuery,
    useSearchAdminPostsQuery
} from '../../../api/client'
import AdminActionBar from '../../../components/admin/AdminActionBar'
import PageMasthead from '../../../components/PageMasthead'
import Pagination from '../../../components/Pagination'
import SearchInput from '../../../components/SearchInput'
import Spinner from '../../../components/Spinner'
import AccountFilter from '../../../components/content/search/AccountFilter'
import SocialMediaServiceFilter from '../../../components/content/search/SocialMediaServiceFilter'
import TeamFilter from '../../../components/content/search/TeamFilter'
import useAdminPostSearchParams from '../../../hooks/useAdminPostSearchParams'
import PostTable from './PostTable'

const PAGE_SIZE = 25

/**
 * Admin > Data Management > Posts. Every post we hold, newest first, with the
 * comments collected under each and how they were moderated. Filters live in
 * the URL so a view can be shared or bookmarked, and so Back undoes a filter.
 */
export default function PostListPage() {
    const { teamIds, accountIds, socialMediaServiceIds, keyword, offset, filtered, update } =
        useAdminPostSearchParams()

    const { data: servicesData } = useGetSocialMediaServicesQuery()
    const allSocialMediaServices = useMemo(
        () => servicesData?.getSocialMediaServices ?? [],
        [servicesData]
    )
    const selectedSocialMediaServices = useMemo(
        () => allSocialMediaServices.filter((s) => socialMediaServiceIds.includes(s.id)),
        [allSocialMediaServices, socialMediaServiceIds]
    )

    const { data, loading, error, previousData } = useSearchAdminPostsQuery({
        variables: {
            params: {
                teamIds,
                accountIds,
                socialMediaServiceIds,
                titleContains: keyword,
                offset,
                limit: PAGE_SIZE
            }
        },
        fetchPolicy: 'cache-and-network'
    })

    // A bookmarked offset can outlive the list it came from (a filter was
    // narrowed, posts were deleted), leaving an empty page beside a non-zero
    // total with no way back but editing the URL. Step back to the last page
    // that exists. Only `data` is trusted here: `previousData` belongs to the
    // offset before this one. Replaced so Back does not return to the dead page.
    const current = data?.searchAdminPosts
    useEffect(() => {
        if (!current || offset === 0 || offset < current.total) return
        const lastPageOffset = Math.max(0, Math.floor((current.total - 1) / PAGE_SIZE) * PAGE_SIZE)
        update({ offset: lastPageOffset }, { replace: true })
    }, [current, offset, update])

    // Keep the last page on screen while the next loads, so paging does not
    // flash the table away.
    const result = current ?? previousData?.searchAdminPosts
    const posts = result?.posts ?? []
    const total = result?.total ?? 0

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-ink-50">
            <PageMasthead
                title="Posts"
                tabTitle="Post admin"
                subtitle="Every post we hold, newest first, with the comments collected under it and how they were moderated"
            />

            <AdminActionBar>
                <TeamFilter
                    teamIds={teamIds}
                    onUpdateSelectedTeams={(ids) => update({ teamIds: ids, accountIds: [] })}
                />
                <SocialMediaServiceFilter
                    allSocialMediaServices={allSocialMediaServices}
                    selectedSocialMediaServices={selectedSocialMediaServices}
                    onSocialMediaServiceListChange={(ids) => update({ socialMediaServiceIds: ids })}
                />
                <AccountFilter
                    selectedAccountIds={accountIds}
                    selectedTeamIds={teamIds}
                    onUpdateSelectedAccounts={(accounts: AccountRecord[]) =>
                        update({ accountIds: accounts.map((a) => a.id) })
                    }
                />
                <div className="w-full max-w-xs">
                    {/* SearchInput debounces onKeywordChange and never fires onSearch
                        on Enter, so the debounced value is the search. */}
                    <SearchInput
                        keyword={keyword}
                        onKeywordChange={(value) => update({ keyword: value }, { replace: true })}
                        onSearch={(value) => update({ keyword: value }, { replace: true })}
                    />
                </div>
                {filtered && (
                    <button
                        type="button"
                        onClick={() =>
                            update({
                                teamIds: [],
                                accountIds: [],
                                socialMediaServiceIds: [],
                                keyword: ''
                            })
                        }
                        className="text-sm font-medium text-ink-500 hover:text-arwen-600"
                    >
                        Clear filters
                    </button>
                )}
                <div className="ml-auto text-sm text-ink-500 tabular-nums">
                    {loading ? (
                        <span className="inline-flex items-center gap-2">
                            <Spinner size={4} />
                            Loading…
                        </span>
                    ) : (
                        `${total.toLocaleString('en-GB')} posts`
                    )}
                </div>
            </AdminActionBar>

            <div className="flex-1 px-6 py-4">
                {error ? (
                    <div className="rounded-lg border border-severe-200 bg-severe-50 p-4 text-sm text-severe-800">
                        Could not load posts. {error.message}
                    </div>
                ) : loading && !result ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size={6} />
                    </div>
                ) : posts.length === 0 ? (
                    <div className="rounded-lg border border-ink-200 bg-white py-16 text-center">
                        <NewspaperIcon
                            className="mx-auto h-10 w-10 text-ink-300"
                            aria-hidden="true"
                        />
                        <p className="mt-3 text-sm text-ink-500">
                            {filtered
                                ? 'No posts for this selection. Post collection is not switched on for every profile.'
                                : 'No posts collected yet.'}
                        </p>
                    </div>
                ) : (
                    // The previous page stays on screen while the next loads, dimmed
                    // and inert, so a filter change reads as "updating" rather than
                    // either a blank flash or a table that has silently not changed.
                    <div
                        aria-busy={loading}
                        className={clsx(
                            'flex flex-col gap-3 transition-opacity duration-150',
                            loading && 'pointer-events-none opacity-50'
                        )}
                    >
                        <PostTable posts={posts} highlight={keyword} />
                        <Pagination
                            offset={offset}
                            limit={PAGE_SIZE}
                            total={total}
                            loading={loading}
                            onChange={(nextOffset) => update({ offset: nextOffset })}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
